import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { getOwnedCompanies, OwnedCompanySummary, setActiveCompanyContextId, getActiveCompanyContextId, SELECTED_COMPANY_ID_STORAGE_KEY } from '../api/apiService';
import { getItem } from '../lib/storage';
import { useAuth } from './AuthContext';
import { track } from '../lib/analytics';
import sentry from '../utils/sentry';

interface CompanyContextValue {
  companies: OwnedCompanySummary[];
  selectedCompanyId: number | null;
  selectedCompany: OwnedCompanySummary | null;
  isLoading: boolean;
  hasLoadedOnce: boolean;
  error: string | null;
  refreshCompanies: () => Promise<void>;
  setSelectedCompany: (companyId: number | null) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue>({
  companies: [],
  selectedCompanyId: null,
  selectedCompany: null,
  isLoading: false,
  hasLoadedOnce: false,
  error: null,
  refreshCompanies: async () => {},
  setSelectedCompany: async () => {},
});

const parseStoredCompanyId = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userToken, isLoading: authLoading } = useAuth();
  const [companies, setCompanies] = useState<OwnedCompanySummary[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true); // Track initial storage restore
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false); // Track if companies have been loaded at least once
  const [error, setError] = useState<string | null>(null);
  const hasRestoredFromStorage = useRef<boolean>(false);
  const restoredCompanyIdFromStorage = useRef<number | null>(null); // Track what was restored from storage
  const lastRestoredUserToken = useRef<string | null>(null); // Track which userToken we restored for
  const hasAttemptedFetch = useRef<boolean>(false); // Track if we've attempted to fetch companies after initialization
  const lastFetchedUserToken = useRef<string | null>(null); // Track which userToken we last fetched companies for
  const previousUserToken = useRef<string | null>(null); // Track previous userToken to detect changes\n
  // Track userToken changes
  useEffect(() => {
    if (previousUserToken.current !== userToken) {
      previousUserToken.current = userToken;
    }
  }, [userToken, authLoading]);

  const ensureSelectedCompany = useCallback(
    async (companyList: OwnedCompanySummary[]) => {
      if (!companyList.length) {
        if (selectedCompanyId !== null) {
          setSelectedCompanyId(null);
        }
        await setActiveCompanyContextId(null);
        return;
      }

      const validIds = new Set(companyList.map((company) => company.id));
      
      // Always check storage DIRECTLY first to preserve user's last selection across reloads
      // Don't rely on cache, read directly from AsyncStorage to ensure we get the latest value
      let storedId: number | null = null;
      let storedIdExists = false;
      try {
        const storedIdStr = await getItem(SELECTED_COMPANY_ID_STORAGE_KEY);
        storedIdExists = storedIdStr !== null;
        storedId = parseStoredCompanyId(storedIdStr);
      } catch (error) {
        console.warn('[CompanyContext] Failed to read from AsyncStorage in ensureSelectedCompany:', error);
        // Fallback to cache
        storedId = parseStoredCompanyId(await getActiveCompanyContextId());
        storedIdExists = storedId !== null;
      }
      
      let nextId: number | null = null;
      let shouldSaveToStorage = false;

      // Priority: 1. Current selectedCompanyId (if valid - user's active choice), 2. Restored ID from storage (if valid), 3. Stored ID (user's last selection from AsyncStorage), 4. First company
      // CRITICAL: Only use restored ID if current selection is null or invalid - don't override user's active selection
      const restoredId = restoredCompanyIdFromStorage.current;
      if (selectedCompanyId && validIds.has(selectedCompanyId)) {
        // Current selection is valid - keep it (user's active choice takes priority)
        nextId = selectedCompanyId;
        // Only save to storage if different from what's stored
        shouldSaveToStorage = storedId !== selectedCompanyId;
      } else if (restoredId !== null && validIds.has(restoredId)) {
        // Restored ID is valid - use it (this preserves selection across reloads when no active selection)
        nextId = restoredId;
        shouldSaveToStorage = false; // Already stored, don't overwrite
      } else if (storedId && validIds.has(storedId)) {
        // Stored ID is valid - use it (this preserves selection across reloads)
        nextId = storedId;
        // Only save if state is different (state might not be updated yet)
        shouldSaveToStorage = nextId !== selectedCompanyId;
      } else {
        // Default to first company only if NO stored ID exists OR stored ID is invalid
        // Only overwrite storage if there was NO stored value before (user's first time)
        if (!storedIdExists) {
          // No stored value exists - this is the first time, so default to first company and save it
          nextId = companyList[0].id;
          shouldSaveToStorage = true;
        } else if (storedId && !validIds.has(storedId)) {
          // Stored ID exists but is invalid (e.g., company was deleted, user lost access)
          // Default to first company and update storage
          nextId = companyList[0].id;
          shouldSaveToStorage = true;
        } else {
          // Should not happen, but fallback to first company
          nextId = companyList[0].id;
          shouldSaveToStorage = !storedIdExists;
        }
      }

      // Update state if different
      if (nextId !== selectedCompanyId) {
        setSelectedCompanyId(nextId);
      }

      // Only save to storage if we determined we should (don't overwrite unnecessarily during restoration)
      if (shouldSaveToStorage) {
        await setActiveCompanyContextId(nextId);
      }
    },
    [selectedCompanyId]
  );

  const refreshCompanies = useCallback(async () => {
    if (!userToken) {
      setCompanies([]);
      setSelectedCompanyId(null);
      setError(null);
      setIsLoading(false);
      setHasLoadedOnce(false);
      hasAttemptedFetch.current = false;
      lastFetchedUserToken.current = null;
      await setActiveCompanyContextId(null);
      return;
    }

    setIsLoading(true);
    setError(null); // Clear any previous error when starting a new request
    hasAttemptedFetch.current = true; // Mark that we've attempted to fetch
    lastFetchedUserToken.current = userToken; // Track which token we're fetching for
    try {
      const response = await getOwnedCompanies();
      if (response.ok) {
        const list = response.data || [];
        setCompanies(list);
        // Pass the restored company ID to ensureSelectedCompany to preserve it
        await ensureSelectedCompany(list);
        setError(null);
        setHasLoadedOnce(true); // Mark that we've successfully loaded companies at least once
      } else {
        const detail = (response.data as any)?.detail;
        console.error('❌ [CompanyContext] Error loading companies:', detail, 'Status:', response.status);
        setError(detail ?? 'Não foi possível carregar as empresas.');
        setHasLoadedOnce(true); // Mark as loaded even on error to prevent infinite loading
      }
    } catch (err) {
      console.error('💥 [CompanyContext] Exception caught:', err);
      setError('Não foi possível carregar as empresas.');
      setHasLoadedOnce(true); // Mark as loaded even on error to prevent infinite loading
    } finally {
      setIsLoading(false);
    }
  }, [ensureSelectedCompany, userToken]);

  // Restore selected company from storage immediately on mount (before fetching companies)
  // This ensures the company ID is available as soon as possible, especially on app reload
  useEffect(() => {
    const restoreStoredCompany = async () => {
      // Don't restore if no userToken - wait for it to be available
      if (!userToken) {
        setIsInitializing(false); // Mark as initialized so we don't block
        return;
      }

      // Only restore once per userToken session (per login)
      // If userToken changed (new login), reset the flags
      if (lastRestoredUserToken.current !== userToken) {
        hasRestoredFromStorage.current = false;
        restoredCompanyIdFromStorage.current = null;
        hasAttemptedFetch.current = false; // Reset fetch attempt flag on new login
        lastFetchedUserToken.current = null; // Reset fetched token on new login
      }

      if (hasRestoredFromStorage.current && lastRestoredUserToken.current === userToken) {
        setIsInitializing(false);
        return;
      }

      try {
        // Read directly from AsyncStorage for immediate restoration (bypassing cache)
        const storedIdStr = await getItem(SELECTED_COMPANY_ID_STORAGE_KEY);
        const parsedId = parseStoredCompanyId(storedIdStr);
        
        if (parsedId !== null) {
          restoredCompanyIdFromStorage.current = parsedId; // Track what was restored
          setSelectedCompanyId(parsedId);
          // Also update the cache in apiService
          await setActiveCompanyContextId(parsedId);
        } else {
          restoredCompanyIdFromStorage.current = null;
        }
      } catch (error) {
        console.warn('[CompanyContext] ⚠️ Failed to restore company from storage:', error);
      } finally {
        hasRestoredFromStorage.current = true;
        lastRestoredUserToken.current = userToken;
        setIsInitializing(false);
      }
    };

    void restoreStoredCompany();
  }, [userToken]);

  // Effect to fetch companies when ready
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Wait for initialization to complete
    if (isInitializing) {
      return;
    }

    // CRITICAL: After auth finishes loading, if we have a userToken, we MUST fetch
    // This handles the case where login saves token to AsyncStorage and AuthContext loads it
    if (userToken && lastFetchedUserToken.current !== userToken) {
      hasAttemptedFetch.current = false;
      refreshCompanies().catch(err => {
        console.error('[CompanyContext] Error in refreshCompanies:', err);
      });
      return;
    }

    // If no userToken after auth has loaded, clear state (logout scenario)
    if (!userToken) {
      // Only clear if we actually had a token before (logout scenario)
      if (previousUserToken.current !== null || lastFetchedUserToken.current !== null) {
        setCompanies([]);
        setSelectedCompanyId(null);
        setError(null);
        setHasLoadedOnce(false);
        hasAttemptedFetch.current = false;
        lastFetchedUserToken.current = null;
      }
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, authLoading, isInitializing]);

  const handleSetSelectedCompany = useCallback(
    async (companyId: number | null) => {
      if (companyId !== null && !companies.some((company) => company.id === companyId)) {
        throw new Error('Empresa não encontrada.');
      }
      // Clear restored ID so it doesn't override user's manual selection
      restoredCompanyIdFromStorage.current = null;
      setSelectedCompanyId(companyId);
      await setActiveCompanyContextId(companyId);

      const selected = companies.find((company) => company.id === companyId);
      track('company_switched', {
        company_id: companyId,
        company_name: selected?.name,
      });
    },
    [companies]
  );

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  // Keep the active company tagged on the Sentry scope so support can pivot an
  // error straight to the tenant. Cleared on logout via AuthContext.clearContext.
  useEffect(() => {
    sentry.setCompanyContext(
      selectedCompany ? { id: selectedCompany.id, name: selectedCompany.name } : null
    );
  }, [selectedCompany]);

  const contextValue = useMemo<CompanyContextValue>(
    () => ({
      companies,
      selectedCompanyId,
      selectedCompany,
      isLoading: isLoading || isInitializing || authLoading, // Only include actual loading states, not hasLoadedOnce
      hasLoadedOnce,
      error,
      refreshCompanies,
      setSelectedCompany: handleSetSelectedCompany,
    }),
    [companies, selectedCompanyId, selectedCompany, isLoading, isInitializing, authLoading, hasLoadedOnce, error, refreshCompanies, handleSetSelectedCompany]
  );

  return <CompanyContext.Provider value={contextValue}>{children}</CompanyContext.Provider>;
};

export const useCompanyContext = () => useContext(CompanyContext);





